import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatPrice } from '../../config/currency';
import { getImageUrl } from '../../config/imageUrl';
import { OrderResponse } from '../../types/order.types';
import { Modal } from '../../components/UI/Modal';
import { useNotification } from '../../contexts/NotificationContext';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderResponse;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const { t, language } = useLanguage();
  const { success } = useNotification();

  // Название источника
  const sourceName = language === 'ru' ? order.source_info.name_ru : order.source_info.name_en;

  // Форматирование даты
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Получить отображаемое значение поля
  const getFieldDisplayValue = (field: { value: string | boolean; value_label_ru?: string; value_label_en?: string }) => {
    if (typeof field.value === 'boolean') {
      return field.value ? (language === 'ru' ? 'Да' : 'Yes') : (language === 'ru' ? 'Нет' : 'No');
    }
    if (field.value_label_ru || field.value_label_en) {
      return language === 'ru' ? field.value_label_ru : field.value_label_en;
    }
    return field.value;
  };

  // Получить лейбл поля
  const getFieldLabel = (field: { label_ru: string; label_en: string }) => {
    return language === 'ru' ? field.label_ru : field.label_en;
  };

  // Копировать значение в буфер обмена
  const copyToClipboard = (value: string | boolean) => {
    const textToCopy = typeof value === 'boolean'
      ? (value ? (language === 'ru' ? 'Да' : 'Yes') : (language === 'ru' ? 'Нет' : 'No'))
      : String(value);

    navigator.clipboard.writeText(textToCopy).then(() => {
      success(language === 'ru' ? 'Скопировано' : 'Copied');
    });
  };

  // Получить номер заказа из деталей (если есть)
  const getOrderNumberFromDetails = () => {
    const formDataEntries = Object.entries(order.form_data);
    for (const [, field] of formDataEntries) {
      const label = language === 'ru' ? field.label_ru : field.label_en;
      if (label.toLowerCase().includes('номер') || label.toLowerCase().includes('number')) {
        return field.value;
      }
    }
    return null;
  };

  const orderNumberFromDetails = getOrderNumberFromDetails();
  const titleSuffix = orderNumberFromDetails ? ` (${orderNumberFromDetails})` : '';
  const modalTitle = `${t('orders.orderDetails')} #${order.id}${titleSuffix}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
    >
      <div className="space-y-6">
        {/* Информация о заказе */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">{t('orders.source')}:</span>
            <span className="text-text-primary font-medium">{sourceName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">{t('orders.createdAt')}:</span>
            <span className="text-text-primary">{formatDate(order.created_at)}</span>
          </div>
        </div>

        {/* Данные клиента */}
        {Object.keys(order.form_data).length > 0 && (
          <div>
            <h3 className="text-md font-semibold text-text-primary mb-3">
              {t('orders.customerInfo')}
            </h3>
            <div className="bg-surface-secondary rounded-lg p-3 space-y-2">
              {Object.entries(order.form_data).map(([key, field]) => (
                <div key={key} className="flex justify-between items-center text-sm group">
                  <span className="text-text-secondary">{getFieldLabel(field)}:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary">{getFieldDisplayValue(field)}</span>
                    <button
                      onClick={() => copyToClipboard(field.value)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-surface-tertiary rounded cursor-pointer"
                      title={language === 'ru' ? 'Копировать' : 'Copy'}
                    >
                      <svg
                        className="w-4 h-4 text-text-secondary hover:text-text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Промокод */}
        {order.promo_info && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <div className="flex justify-between text-sm">
              <span className="text-green-700 dark:text-green-400">
                {t('orders.promoCode')}: {order.promo_info.code}
              </span>
              <span className="text-green-700 dark:text-green-400 font-medium">
                -{order.promo_info.discount_percent}%
              </span>
            </div>
          </div>
        )}

        {/* Позиции заказа */}
        <div>
          <h3 className="text-md font-semibold text-text-primary mb-3">
            {t('orders.items')}
          </h3>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 bg-surface-secondary rounded-lg p-3"
              >
                {/* Картинка */}
                {getImageUrl(item.dish_image) ? (
                  <img
                    src={getImageUrl(item.dish_image)!}
                    alt={item.dish_name}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-surface-tertiary flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🍽️</span>
                  </div>
                )}

                {/* Информация */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">
                    {item.dish_name}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {item.weight}{t('addOrder.grams')} × {item.quantity}
                  </div>
                  {item.promo_applied && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      {t('addOrder.promoAppliedToPortion')}
                    </span>
                  )}
                  {item.item_discount_percent && item.item_discount_percent > 0 && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                      {t('addOrder.manualDiscount')}: -{item.item_discount_percent}%
                    </span>
                  )}
                </div>

                {/* Цена */}
                <div className="text-right flex-shrink-0">
                  <div className="font-medium text-text-primary">
                    {formatPrice(item.final_price * item.quantity)}
                  </div>
                  {item.original_price !== item.final_price && (
                    <div className="text-xs text-text-secondary line-through">
                      {formatPrice(item.original_price * item.quantity)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Итого */}
        <div className="border-t border-border-primary pt-4 space-y-2">
          {order.subtotal !== order.total && (
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">{t('orders.subtotal')}:</span>
              <span className="text-text-secondary line-through">
                {formatPrice(order.subtotal)}
              </span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold">
            <span className="text-text-primary">{t('orders.total')}:</span>
            <span className="text-text-primary">{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};
